import { EXTERNAL_LINK_ON_DEMAND_CONTENT_TYPE } from '~/clc/features/academy-events-v2/constants';
import { APP_CONSTANTS } from '~/common/explicit-exports';

export const ACADEMY_LEARNER_PATHS = {
  academy: () => '',
  certifications: () => `/certifications`,
  certification: (certificationId) => `${ACADEMY_LEARNER_PATHS.certifications()}/${certificationId}`,
  certificationContent(certificationId, certificationContentId) {
    return `${ACADEMY_LEARNER_PATHS.trainings()}/${certificationContentId}/overview?certification_id=${certificationId}`;
  },
  paths: () => `/paths`,
  path: (pathId) => `${ACADEMY_LEARNER_PATHS.paths()}/${pathId}`,
  resource: (resourceId) => `/resources/${resourceId}`,
  trainings: () => `/training`,
  training: (trainingId) => `${ACADEMY_LEARNER_PATHS.trainings()}/${trainingId}/overview`,
  collection: (collectionId) => `/collections/${collectionId}`,
  events: () => `/events`,
  event: (eventId) => `${ACADEMY_LEARNER_PATHS.events()}/${eventId}`,
  eventOnDemandContent: (eventId, onDemandContentId) =>
    `${ACADEMY_LEARNER_PATHS.event(eventId)}/on_demand_content/${onDemandContentId}`,
  eventOnDemandContentExternal: (contentId) => `/view_external_on_demand_content/${contentId}`,
  customPage: (slug: string) => `/pages/${slug}`,
  scorm: (scormId) => `/scorm/${scormId}`,
};

const TRAINING_TYPES: string[] = [
  APP_CONSTANTS.ASSET_PICKER_TYPES.GUIDE,
  APP_CONSTANTS.ASSET_PICKER_TYPES.RESOURCE,
  APP_CONSTANTS.ASSET_PICKER_TYPES.SCORM,
  APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_COLLECTION_CONTENT,
];
interface AcademiesViewUrlType {
  type: string;
  id: string;
  parentContentId?: string;
  parentId?: string;
  contentId?: string;
  contentType?: string;
  slug?: string;
}

export const getAcademiesViewUrl = ({
  type,
  id,
  parentContentId,
  parentId,
  contentId,
  contentType,
  slug,
}: AcademiesViewUrlType) => {
  // need special type constant until we fix object_type mapping with custom page objects
  if ([APP_CONSTANTS.ASSET_PICKER_TYPES.ACADEMY_CERTIFICATION, 'certification'].includes(type)) {
    return ACADEMY_LEARNER_PATHS.certification(id);
  }

  if ([APP_CONSTANTS.ASSET_PICKER_TYPES.ACADEMY_PATH, 'path'].includes(type)) {
    return ACADEMY_LEARNER_PATHS.path(id);
  }

  // need special type constant until we fix object_type mapping with custom page objects
  if ([APP_CONSTANTS.ASSET_PICKER_TYPES.ACADEMY_COLLECTION, 'collection'].includes(type)) {
    return ACADEMY_LEARNER_PATHS.collection(id);
  }

  // need special type constant until we fix object_type mapping with custom page objects
  if ([APP_CONSTANTS.ASSET_PICKER_TYPES.ACADEMY_EVENT, 'event'].includes(type)) {
    return ACADEMY_LEARNER_PATHS.event(id);
  }

  if (type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_CERTIFICATION_CONTENT) {
    return ACADEMY_LEARNER_PATHS.certificationContent(parentContentId, id);
  }

  if (type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_PATH_CONTENT) {
    return ACADEMY_LEARNER_PATHS.training(id);
  }

  if (type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_EVENT_SESSION) {
    return ACADEMY_LEARNER_PATHS.event(parentContentId || parentId);
  }

  if (type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_EVENT_ON_DEMAND_CONTENT) {
    if (contentType === EXTERNAL_LINK_ON_DEMAND_CONTENT_TYPE) {
      return ACADEMY_LEARNER_PATHS.eventOnDemandContentExternal(id);
    }

    return ACADEMY_LEARNER_PATHS.eventOnDemandContent(parentContentId, id);
  }

  if (TRAINING_TYPES.includes(type)) {
    if (contentType === APP_CONSTANTS.ASSET_PICKER_TYPES.RESOURCE) {
      return ACADEMY_LEARNER_PATHS.resource(id);
    }
    if (contentType === APP_CONSTANTS.ASSET_PICKER_TYPES.SCORM) {
      return ACADEMY_LEARNER_PATHS.scorm(id);
    }
    return ACADEMY_LEARNER_PATHS.training(id);
  }

  if (type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_CUSTOM_PAGE) {
    return ACADEMY_LEARNER_PATHS.customPage(slug);
  }

  if (type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_CUSTOM_PAGE_OBJECT) {
    const url = getAcademiesViewUrl({
      type: contentType,
      id: contentId,
      parentId,
      contentId,
      contentType,
    });

    return `${ACADEMY_LEARNER_PATHS.customPage(slug)}${url}`;
  }

  return ACADEMY_LEARNER_PATHS.academy();
};

export const getAcademiesViewTarget = (type: string, contentType?: string) => {
  if (
    type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_EVENT_ON_DEMAND_CONTENT &&
    contentType === EXTERNAL_LINK_ON_DEMAND_CONTENT_TYPE
  ) {
    return '_blank';
  }

  return '_self';
};
